export function normalizeLatexMathDelimiters(markdown: string): string {
  return splitFencedCode(markdown)
    .map((segment) =>
      segment.isCode ? segment.value : normalizeUnfencedMath(segment.value),
    )
    .join("");
}

type MarkdownSegment = {
  isCode: boolean;
  value: string;
};

function splitFencedCode(markdown: string): MarkdownSegment[] {
  const lines = markdown.match(/.*(?:\n|$)/g)?.filter(Boolean) ?? [];
  const segments: MarkdownSegment[] = [];
  let buffered = "";
  let fence: { character: "`" | "~"; length: number } | null = null;

  const flush = (isCode: boolean) => {
    if (!buffered) return;
    segments.push({ isCode, value: buffered });
    buffered = "";
  };

  for (const line of lines) {
    if (!fence) {
      const openingFence = line.match(/^ {0,3}(`{3,}|~{3,})/u)?.[1];
      if (!openingFence) {
        buffered += line;
        continue;
      }
      flush(false);
      fence = {
        character: openingFence[0] as "`" | "~",
        length: openingFence.length,
      };
      buffered = line;
      continue;
    }

    buffered += line;
    const closingPattern = new RegExp(
      `^ {0,3}${fence.character === "`" ? "`" : "~"}{${fence.length},}[ \\t]*(?:\\r?\\n)?$`,
      "u",
    );
    if (closingPattern.test(line)) {
      flush(true);
      fence = null;
    }
  }

  flush(Boolean(fence));
  return segments;
}

function normalizeUnfencedMath(markdown: string): string {
  let normalized = "";
  let index = 0;

  while (index < markdown.length) {
    if (markdown[index] === "`") {
      const runLength = countRun(markdown, index, "`");
      const marker = "`".repeat(runLength);
      const closingIndex = markdown.indexOf(marker, index + runLength);
      if (closingIndex === -1) {
        normalized += markdown.slice(index);
        break;
      }
      const end = closingIndex + runLength;
      normalized += markdown.slice(index, end);
      index = end;
      continue;
    }

    // Only command-led single-dollar spans are unambiguous math. Keep ordinary
    // currency, escaped dollars, code, and existing $$ delimiters untouched.
    if (
      markdown[index] === "$" &&
      markdown[index - 1] !== "$" &&
      !isEscaped(markdown, index)
    ) {
      const math = markdown
        .slice(index)
        .match(/^\$(\\[A-Za-z]+(?:\\[^\r\n]|[^$`\r\n\\])*)\$(?!\$)/u);
      if (math) {
        normalized += `$$${math[1]}$$`;
        index += math[0].length;
        continue;
      }
    }

    const delimiter = latexDelimiterAt(markdown, index);
    if (!delimiter) {
      normalized += markdown[index];
      index += 1;
      continue;
    }

    const closingIndex = findClosingDelimiter(
      markdown,
      index + delimiter.open.length,
      delimiter.close,
    );
    if (closingIndex === -1) {
      normalized += delimiter.open;
      index += delimiter.open.length;
      continue;
    }

    const content = markdown
      .slice(index + delimiter.open.length, closingIndex)
      .trim();
    // Models also write \( and \[ as Markdown-escaped brackets, as in
    // "\(UTC\)" or "\[1\]"; those stay literal so Markdown shows the bracket.
    if (!readsAsMath(content)) {
      normalized += delimiter.open;
      index += delimiter.open.length;
      continue;
    }
    normalized += delimiter.display
      ? `\n\n$$\n${content}\n$$\n\n`
      : `$$${content}$$`;
    index = closingIndex + delimiter.close.length;
  }

  return normalized;
}

/**
 * Bracketed content is math when it carries a TeX command, a math operator
 * or is a lone variable. Prose words or bare numbers ("2026-08-01 through
 * 2026-08-31 UTC", "1") mean the brackets were escaped text.
 */
function readsAsMath(content: string): boolean {
  if (/\\[A-Za-z]+/u.test(content)) return true;
  if (/[A-Za-z]{3,}/u.test(content)) return false;
  return /[\^_=<>+*/{}|]|\s-\s|^[A-Za-z]$/u.test(content);
}

function latexDelimiterAt(markdown: string, index: number) {
  if (isEscaped(markdown, index)) return null;
  if (markdown.startsWith("\\[", index)) {
    return { close: "\\]", display: true, open: "\\[" } as const;
  }
  if (markdown.startsWith("\\(", index)) {
    return { close: "\\)", display: false, open: "\\(" } as const;
  }
  return null;
}

function findClosingDelimiter(
  markdown: string,
  start: number,
  delimiter: "\\]" | "\\)",
): number {
  let index = start;
  while (index < markdown.length) {
    if (markdown[index] === "`") {
      const runLength = countRun(markdown, index, "`");
      const marker = "`".repeat(runLength);
      const closingIndex = markdown.indexOf(marker, index + runLength);
      if (closingIndex === -1) return -1;
      index = closingIndex + runLength;
      continue;
    }
    if (markdown.startsWith(delimiter, index) && !isEscaped(markdown, index)) {
      return index;
    }
    index += 1;
  }
  return -1;
}

function countRun(markdown: string, start: number, character: string): number {
  let index = start;
  while (markdown[index] === character) index += 1;
  return index - start;
}

function isEscaped(markdown: string, index: number): boolean {
  let slashCount = 0;
  for (
    let cursor = index - 1;
    cursor >= 0 && markdown[cursor] === "\\";
    cursor -= 1
  ) {
    slashCount += 1;
  }
  return slashCount % 2 === 1;
}
